import React, { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { templateParser, formatPrice } from 'utils/formatString';
import { omit, sum } from 'ramda';
import { connect } from 'react-redux';

import {
  Popup,
  Heading,
  Checkbox,
  Button,
  HTML,
} from 'components';
import BuyPackagesRow from '../BuyPackagesRow';
import { getAutoProlongText } from '../../utils';
import ConnectionService from '../ConnectionService';
import withServiceConnection from '../../hocs/withServiceConnection';

import styles from './styles.pcss';

const cx = classNames.bind(styles);

const BuyPackagesPopup = (props) => {
  const {
    onClose,
    onSetPending,
    packages,
    content,
    initServiceConnection,
    connectServicesUrl,
    connectServiceUrl,
    connectionContent,
  } = props;
  const [selectedPackages, setSelectedPackages] = useState({});
  const [checkedAutoProlongs, setCheckedAutoProlongs] = useState({});
  const [isShowConnectionService, setIsShowConnectionService] = useState(false);

  const onPackageChange = (val, type) => {
    setSelectedPackages((prev) => ({ ...prev, [type]: val }));
    if (val && val.socAutoProlong) {
      setCheckedAutoProlongs((prev) => ({
        ...prev,
        [type]: {
          socAutoProlong: val.socAutoProlong,
          isChecked: true,
        },
      }));
    } else {
      setCheckedAutoProlongs((prev) => omit([type], prev));
    }
  };

  const onAutoProlongCheckboxChange = (type) => {
    setCheckedAutoProlongs((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        isChecked: !prev[type].isChecked,
      },
    }));
  };

  const selectedPackagesValues = useMemo(
    () => Object.values(selectedPackages).filter((v) => v),
    [selectedPackages],
  );

  const hasSelectedPackage = !!selectedPackagesValues.length;
  const totalPrice = useMemo(
    () => sum(selectedPackagesValues.map(({ priceValue }) => priceValue)),
    [selectedPackagesValues],
  );

  const onOrderClick = () => {
    const checkedAutoProlongsValues = Object.values(checkedAutoProlongs);
    const filteredAutoProlongsValues = checkedAutoProlongsValues.filter((item) => item.isChecked);
    if (selectedPackagesValues.length > 1 || filteredAutoProlongsValues.length) {
      initServiceConnection(connectServicesUrl, {
        socs: selectedPackagesValues
          .map((p) => p.soc)
          .concat(filteredAutoProlongsValues.map((a) => a.socAutoProlong)),
      });
    } else {
      initServiceConnection(connectServiceUrl, {
        soc: selectedPackagesValues[0].soc,
      });
    }
    setIsShowConnectionService(true);
  };

  const onSuccess = () => {
    onClose();
  };

  return (
    <Popup opened onClose={onClose}>
      {isShowConnectionService ? (
        <ConnectionService
          onSuccess={onSuccess}
          onSetPending={onSetPending}

          onTimeoutBtnClick={onClose}
          onErrorBtnClick={onClose}
          content={connectionContent}
          serviceName={content.servicesName}
        />
      ) : (
        <>
          <Heading level={1} isStyled={false}>
            <HTML>{content.title}</HTML>
          </Heading>
          <HTML as="div" className={cx('subTitle')}>{content.subTitle}</HTML>
          <div className={cx('packagesWrapper')}>
            {packages.map(({ parameters, type }, i) => {
              const autoProlong = checkedAutoProlongs[type];
              const selectedPackage = selectedPackages[type];

              return (
                <div key={i} className={cx('packageWrap')}>
                  <BuyPackagesRow
                    title={content.packagesTitles[type.toLowerCase()]}
                    packages={parameters}
                    onChange={(val) => onPackageChange(val, type)}
                  />
                  {selectedPackage && autoProlong && (
                    <Checkbox
                      name={`${type}-package-autoprolong`}
                      className={cx('checkbox')}
                      checked={autoProlong.isChecked}
                      handleChange={() => onAutoProlongCheckboxChange(type)}
                    >
                      <HTML>{getAutoProlongText(content.autoProlongCheckboxs, type, selectedPackage)}</HTML>
                    </Checkbox>
                  )}
                </div>
              );
            })}
            <div className={cx('btnWrap')}>
              <Button
                disabled={!hasSelectedPackage}
                onClick={onOrderClick}
                type="submit"
                className="big"
                wide
              >
                <HTML>
                  {content.orderBtn}
                  {totalPrice
                    ? templateParser(content.orderBtnPostfix, { price: formatPrice(totalPrice) })
                    : ''}
                </HTML>
              </Button>
            </div>
          </div>
        </>
      )}
    </Popup>
  );
};

BuyPackagesPopup.propTypes = {
  packages: PropTypes.arrayOf(PropTypes.shape({
    parameters: PropTypes.array,
    type: PropTypes.string,
  })),
  content: PropTypes.shape({
    orderBtnPostfix: PropTypes.string,
    servicesName: PropTypes.string,
    title: PropTypes.string,
    subTitle: PropTypes.string,
    packagesTitles: PropTypes.objectOf(PropTypes.string),
    autoProlongCheckboxs: PropTypes.objectOf(PropTypes.string),
    orderBtn: PropTypes.string,
  }),
  connectServicesUrl: PropTypes.string,
  connectServiceUrl: PropTypes.string,
  connectionContent: PropTypes.object,
  onClose: PropTypes.func,
  initServiceConnection: PropTypes.func,
  onSetPending: PropTypes.func,
};

const mapStateToProps = (state) => {
  const { connectServiceUrl, connectServicesUrl, yonth } = state.home.tariffServices.data.content;
  return {
    progressBarLimits: state.home.accumulators.data.progressBarLimits,
    connectServiceUrl,
    connectServicesUrl,
    connectionContent: yonth.connectionService,
  };
};

const BuyPackagesPopupWithServiceConnection = withServiceConnection(BuyPackagesPopup);

export default connect(mapStateToProps)(BuyPackagesPopupWithServiceConnection);
